/**
 * Hook for checking whether the test-login flow is enabled in the current environment.
 *
 * Calls the unauthenticated backend config endpoint and caches the result for 5 minutes.
 * Returns `enabled: false` and `requireToken: true` on error or while loading so the
 * UI defaults to hiding the test-login button and (when a stale cache shows the modal)
 * requiring the token field.
 *
 * `requireToken` is `false` only when running locally — the backend bypasses the
 * bearer token check in that environment, and the modal hides the token input.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchWithDefaults } from '@/lib/http/client';

interface TestLoginConfigResponse {
  enabled: boolean;
  // Optional: older deploys may not return this field. Treat missing as `true`
  // (token required) so the UI fails closed when talking to a stale backend.
  require_token?: boolean;
}

export function useTestLoginEnabled(): {
  enabled: boolean;
  requireToken: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['testLoginEnabled'],
    queryFn: async (): Promise<TestLoginConfigResponse> => {
      const response = await fetchWithDefaults(
        '/api/test-integration/test-login/config/'
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch test-login config: ${response.status}`
        );
      }
      return (await response.json()) as TestLoginConfigResponse;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    enabled: data?.enabled ?? false,
    requireToken: data?.require_token ?? true,
    isLoading,
  };
}
